import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addToast } from '../store/ducks/toast/actions';
import { EToastTypes } from '../store/ducks/toast/types';
import { NewCartItem } from '../services/MVP/models';
import { useWishlistService } from '../services/MVP/useWishlistService';
import { sendTagging } from '../store/ducks/tagging/actions';

export function useCartWishlist(items: NewCartItem[]) {
  const dispatch = useDispatch();
  const { addToWishlist, removeFromWishlist } = useWishlistService();

  const buildFavorited = (list: NewCartItem[]) =>
    new Set(
      list
        .filter((i) => i.extension_attributes.added_to_wishlist)
        .map((i) => i.sku)
    );

  const [favoritedSkus, setFavoritedSkus] = useState<Set<string>>(() =>
    buildFavorited(items)
  );
  const favoritedSkusRef = useRef<Set<string>>(favoritedSkus);
  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  const isInitializedRef = useRef(items.length > 0);
  useEffect(() => {
    if (items.length > 0 && !isInitializedRef.current) {
      isInitializedRef.current = true;
      const newFavorited = buildFavorited(items);
      favoritedSkusRef.current = newFavorited;
      setFavoritedSkus(new Set(newFavorited));
    }
  }, [items]);

  const handleToggleFavorite = (sku: string) => {
    const willFavorite = !favoritedSkusRef.current.has(sku);

    const next = new Set(favoritedSkusRef.current);
    if (willFavorite) next.add(sku);
    else next.delete(sku);
    favoritedSkusRef.current = next;
    setFavoritedSkus(new Set(next));

    const cartItem = items.find((i) => String(i.sku) === String(sku));
    dispatch(
      sendTagging({
        event: willFavorite ? 'adicionar_favorito' : 'remover_favorito',
        screenName: 'carrinho',
        ecommerce: {
          items: [
            {
              item_id: String(sku),
              item_name: cartItem?.name ?? '',
              item_category: cartItem?.descricao_gpm ?? '',
              ...(cartItem?.price?.creditPrice &&
              Number(cartItem.price.creditPrice) > 0
                ? { price: String(cartItem.price.creditPrice) }
                : {}),
              quantity: String(cartItem?.qty ?? 1),
              unidade_de_medida:
                cartItem?.extension_attributes?.measure_unity_gerdau ?? '',
              cod_gerdau: String(sku),
              preco_acordado:
                (cartItem?.extension_attributes
                  ?.has_agreed_price_gerdau as boolean) ?? false,
              screenName: 'carrinho',
              tipo_compra: 'carrinho',
              favoritado: willFavorite
            }
          ]
        }
      })
    );

    const existing = debounceTimers.current.get(sku);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(async () => {
      debounceTimers.current.delete(sku);
      const success = willFavorite
        ? await addToWishlist(sku)
        : await removeFromWishlist(sku);

      if (success) {
        dispatch(
          addToast({
            type: willFavorite ? EToastTypes.SUCCESS : EToastTypes.DANGER,
            message: willFavorite
              ? 'Produto adicionado aos Favoritos'
              : 'Produto removido dos Favoritos',
            durationInMS: 5000
          })
        );
      } else {
        const revert = new Set(favoritedSkusRef.current);
        if (willFavorite) revert.delete(sku);
        else revert.add(sku);
        favoritedSkusRef.current = revert;
        setFavoritedSkus(new Set(revert));
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message:
              'Não foi possível atualizar os favoritos. Tente novamente.',
            durationInMS: 5000
          })
        );
      }
    }, 300);

    debounceTimers.current.set(sku, timer);
  };

  return { favoritedSkus, handleToggleFavorite };
}
